import React, { useState } from 'react';
import { COLORS, DARK_THEME } from '../../constants/colors';
import { FLAGS } from '../../constants/flags';
import { useTheme } from '../../context/ThemeContext';
import memberStatesHeroImage from '../../assets/pages-hero/member-states.png';

const MemberStates = () => {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const { isDarkMode } = useTheme();

  const countries = [
    {
      name: 'Republic of Türkiye',
      shortName: 'Türkiye',
      capital: 'Ankara',
      population: '85 Million',
      area: '783,356 km²',
      gdp: '$905 Billion',
      flag: FLAGS.turkey,
      color: COLORS.turkey,
      leader: 'President Recep Tayyip Erdoğan',
      joined: 'Founding Member (2009)',
      description: 'A transcontinental country bridging Europe and Asia, Türkiye is a founding member and key driver of OTS initiatives.',
      highlights: ['G20 Member', 'NATO Ally', 'Major Economy', 'Cultural Hub'],
      keyFacts: [
        'Largest economy among member states',
        'Strategic Bosphorus strait location',
        'Rich Ottoman and Turkic heritage',
        'Major tourism destination'
      ],
    },
    {
      name: 'Republic of Azerbaijan',
      shortName: 'Azerbaijan',
      capital: 'Baku',
      population: '10 Million',
      area: '86,600 km²',
      gdp: '$54 Billion',
      flag: FLAGS.azerbaijan,
      color: COLORS.azerbaijan,
      leader: 'President Ilham Aliyev',
      joined: 'Founding Member (2009)',
      description: 'Located at the crossroads of Eastern Europe and Western Asia, Azerbaijan is rich in natural resources and has emerged as a key energy partner in the region.',
      highlights: ['Energy Hub', 'Caspian Gateway', 'Tech Growth', 'F1 Host'],
      keyFacts: [
        'Major oil and gas exporter',
        'Hosted Eurovision 2012',
        'Flame Towers landmark',
        'Ancient Zoroastrian heritage'
      ],
    },
    {
      name: 'Republic of Kazakhstan',
      shortName: 'Kazakhstan',
      capital: 'Astana',
      population: '19 Million',
      area: '2,724,900 km²',
      gdp: '$220 Billion',
      flag: FLAGS.kazakhstan,
      color: COLORS.kazakhstan,
      leader: 'President Kassym-Jomart Tokayev',
      joined: 'Founding Member (2009)',
      description: 'The world\'s largest landlocked country, Kazakhstan is a vital corridor of the New Silk Road and possesses vast natural resources.',
      highlights: ['Largest Territory', 'Resource Rich', 'Space Industry', 'New Capital'],
      keyFacts: [
        'Baikonur Cosmodrome host',
        'Major uranium producer',
        'Modern capital Astana',
        'Vast steppes and mountains'
      ],
    },
    {
      name: 'Kyrgyz Republic',
      shortName: 'Kyrgyzstan',
      capital: 'Bishkek',
      population: '7 Million',
      area: '199,951 km²',
      gdp: '$9 Billion',
      flag: FLAGS.kyrgyzstan,
      color: COLORS.kyrgyzstan,
      leader: 'President Sadyr Japarov',
      joined: 'Founding Member (2009)',
      description: 'Known for its stunning mountain landscapes and rich nomadic heritage, Kyrgyzstan preserves ancient Turkic traditions and culture.',
      highlights: ['Mountain Tourism', 'Nomadic Culture', 'Hydro Power', 'Epic Poetry'],
      keyFacts: [
        'Issyk-Kul lake destination',
        'Manas epic heritage',
        'Traditional yurt culture',
        'Adventure tourism hub'
      ],
    },
    {
      name: 'Republic of Uzbekistan',
      shortName: 'Uzbekistan',
      capital: 'Tashkent',
      population: '35 Million',
      area: '448,978 km²',
      gdp: '$80 Billion',
      flag: FLAGS.uzbekistan,
      color: COLORS.uzbekistan,
      leader: 'President Shavkat Mirziyoyev',
      joined: 'Founding Member (2009)',
      description: 'Home to legendary Silk Road cities like Samarkand and Bukhara, Uzbekistan is the cultural heart of Central Asia with UNESCO World Heritage sites.',
      highlights: ['Historic Cities', 'Silk Road Heritage', 'Growing Economy', 'Cultural Center'],
      keyFacts: [
        'Samarkand, Bukhara, Khiva',
        'Registan Square landmark',
        'Islamic architecture gems',
        'Major cotton producer'
      ],
    },
  ];

  const observers = [
    {
      name: 'Hungary',
      flag: FLAGS.hungary,
      joined: '2018',
      status: 'Observer',
      note: 'First European observer state',
    },
    {
      name: 'Turkmenistan',
      flag: FLAGS.turkmenistan,
      joined: '2021',
      status: 'Observer',
      note: 'Extensive natural gas reserves',
    },
    {
      name: 'Turkish Republic of Northern Cyprus',
      flag: FLAGS.trnc,
      joined: '2022',
      status: 'Observer',
      note: 'Strategic Mediterranean location',
    },
    {
      name: 'Economic Cooperation Organization',
      flag: FLAGS.eco,
      joined: '2023',
      status: 'Partner Organization',
      note: 'Regional economic cooperation',
    },
  ];

  return (
    <div style={styles.page}>
      {/* Hero Section */}
      <section style={{
        ...styles.hero,
        background: isDarkMode 
          ? `linear-gradient(135deg, ${DARK_THEME.background}DD 0%, ${DARK_THEME.backgroundTertiary}DD 100%), url(${memberStatesHeroImage})`
          : `linear-gradient(135deg, ${COLORS.primary}DD 0%, ${COLORS.dark}DD 100%), url(${memberStatesHeroImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundBlendMode: 'multiply',
      }}>
        <div style={styles.heroOverlay}></div>
        <div style={styles.heroContent}>
          <span style={{
            ...styles.badge,
            backgroundColor: isDarkMode ? `${DARK_THEME.primary}20` : 'rgba(255,255,255,0.15)',
          }}>Member States</span>
          <h1 style={styles.heroTitle}>United Turkic Nations</h1>
          <p style={styles.heroSubtitle}>Building bridges between nations through shared heritage and common vision</p>
          <div style={styles.flagsRow}>
            {countries.map((c, i) => (
              <img key={i} src={c.flag} alt={c.shortName} style={styles.heroFlag} />
            ))}
          </div>
        </div>
      </section>

      {/* Countries Grid */}
      <section style={{
        ...styles.section,
        backgroundColor: isDarkMode ? DARK_THEME.background : COLORS.white,
      }}>
        <div style={styles.container}>
          <div style={styles.countriesGrid}>
            {countries.map((country, index) => (
              <div
                key={index}
                style={{
                  ...styles.countryCard,
                  backgroundColor: isDarkMode ? DARK_THEME.surface : COLORS.white,
                  boxShadow: isDarkMode ? `0 4px 25px ${DARK_THEME.shadow}` : '0 4px 25px rgba(0,0,0,0.08)',
                  borderTop: `5px solid ${country.color}`,
                }}
                onClick={() => setSelectedCountry(selectedCountry === index ? null : index)}
              >
                <div style={styles.cardHeader}>
                  <img src={country.flag} alt={country.shortName} style={styles.cardFlag} />
                  <div>
                    <h3 style={{...styles.countryName, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
                      {country.shortName}
                    </h3>
                    <span style={{...styles.capital, color: isDarkMode ? DARK_THEME.textMuted : COLORS.gray500}}>
                      {country.capital}
                    </span>
                  </div>
                </div>

                <div style={{
                  ...styles.statsGrid,
                  backgroundColor: isDarkMode ? DARK_THEME.backgroundSecondary : COLORS.light,
                }}>
                  {
                  [{
                    val: country.population,
                    label: 'Population'
                  }, {
                    val: country.area,
                    label: 'Area'
                  }, {
                    val: country.gdp,
                    label: 'GDP'
                  }].map((stat, idx) => (
                    <div key={idx} style={styles.stat}>
                      <span style={{...styles.statValue, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
                        {stat.val}
                      </span>
                      <span style={{...styles.statLabel, color: isDarkMode ? DARK_THEME.textMuted : COLORS.gray500}}>
                        {stat.label}
                      </span>
                    </div>
                  ))}

                </div>

                <p style={{...styles.description, color: isDarkMode ? DARK_THEME.textSecondary : COLORS.gray700}}>
                  {country.description}
                </p>

                <div style={styles.highlights}>
                  {country.highlights.map((h, idx) => (
                    <span key={idx} style={{...styles.highlightTag, backgroundColor: `${country.color}15`, color: country.color}}>
                      {h}
                    </span>
                  ))}
                </div>

                {selectedCountry === index && (
                  <div style={styles.expandedContent}>
                    <div style={{...styles.divider, backgroundColor: isDarkMode ? DARK_THEME.border : COLORS.gray300}}></div>
                    <p style={{...styles.leaderInfo, color: isDarkMode ? DARK_THEME.textSecondary : COLORS.gray700}}>
                      <strong>Leader:</strong> {country.leader}
                    </p>
                    <p style={{...styles.joinedInfo, color: isDarkMode ? DARK_THEME.textSecondary : COLORS.gray700}}>
                      <strong>Joined:</strong> {country.joined}
                    </p>
                    <h4 style={{...styles.keyFactsTitle, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>Key Facts</h4>
                    <ul style={styles.keyFactsList}>
                      {country.keyFacts.map((fact, idx) => (
                        <li key={idx} style={{...styles.keyFact, color: isDarkMode ? DARK_THEME.textSecondary : COLORS.gray600}}>
                          • {fact}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <button style={{
                  ...styles.expandButton,
                  borderColor: isDarkMode ? DARK_THEME.border : COLORS.gray300,
                  color: isDarkMode ? DARK_THEME.primary : COLORS.primary,
                  backgroundColor: isDarkMode ? DARK_THEME.surfaceHover : 'transparent',
                }}>
                  {selectedCountry === index ? 'Show Less' : 'Learn More'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Observer States */}
      <section style={{
        ...styles.sectionAlt,
        backgroundColor: isDarkMode ? DARK_THEME.backgroundSecondary : COLORS.light,
      }}>
        <div style={styles.container}>
          <div style={styles.header}>
            <span style={{
              ...styles.badgeSecondary,
              backgroundColor: isDarkMode ? `${DARK_THEME.secondary}30` : `${COLORS.secondary}20`,
              color: isDarkMode ? DARK_THEME.secondary : COLORS.secondary,
            }}>Partners</span>
            <h2 style={{...styles.sectionTitle, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
              Observer States & Partners
            </h2>
            <p style={{...styles.sectionSubtitle, color: isDarkMode ? DARK_THEME.textSecondary : COLORS.gray600}}>
              Nations and organizations participating in OTS activities
            </p>
          </div>
          <div style={styles.observersGrid}>
            {observers.map((obs, index) => (
              <div key={index} style={{
                ...styles.observerCard,
                backgroundColor: isDarkMode ? DARK_THEME.surface : COLORS.white,
                boxShadow: isDarkMode ? `0 4px 20px ${DARK_THEME.shadow}` : '0 4px 20px rgba(0,0,0,0.06)',
              }}>
                <img src={obs.flag} alt={obs.name} style={styles.observerFlag} />
                <h3 style={{...styles.observerName, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
                  {obs.name}
                </h3>
                <span style={{...styles.observerStatus, color: isDarkMode ? DARK_THEME.primary : COLORS.primary}}>
                  {obs.status} since {obs.joined}
                </span>
                <p style={{...styles.observerNote, color: isDarkMode ? DARK_THEME.textSecondary : COLORS.gray600}}>
                  {obs.note}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

const styles = {
  page: { paddingTop: '0px' },
  hero: {
    height: '50vh',
    minHeight: '400px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    textAlign: 'center',
  },
  heroOverlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M30 0L60 30L30 60L0 30z\' fill=\'rgba(255,255,255,0.03)\'/%3E%3C/svg%3E")',
  },
  heroContent: { position: 'relative', zIndex: 2, padding: '0 20px' },
  badge: { display: 'inline-block', color: COLORS.white, padding: '8px 24px', borderRadius: '50px', fontSize: '14px', fontWeight: '600', marginBottom: '20px' },
  badgeSecondary: { display: 'inline-block', padding: '8px 24px', borderRadius: '50px', fontSize: '14px', fontWeight: '600', marginBottom: '20px' },
  heroTitle: { fontSize: 'clamp(36px, 6vw, 56px)', color: COLORS.white, fontWeight: '700', marginBottom: '20px' },
  heroSubtitle: { fontSize: '18px', color: 'rgba(255,255,255,0.85)', marginBottom: '40px', lineHeight: '1.7' },
  flagsRow: { display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' },
  heroFlag: { height: '50px', width: 'auto', borderRadius: '6px', boxShadow: '0 4px 15px rgba(0,0,0,0.3)' },
  section: { padding: '80px 20px' },
  sectionAlt: { padding: '80px 20px' },
  container: { maxWidth: '1300px', margin: '0 auto' },
  header: { textAlign: 'center', marginBottom: '50px' },
  sectionTitle: { fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: '700', marginBottom: '15px' },
  sectionSubtitle: { fontSize: '16px' },
  countriesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '30px' },
  countryCard: { borderRadius: '20px', padding: '30px', transition: 'all 0.3s ease', cursor: 'pointer' },
  cardHeader: { display: 'flex', alignItems: 'center', gap: '18px', marginBottom: '25px' },
  cardFlag: { height: '56px', width: 'auto', borderRadius: '8px', boxShadow: '0 3px 12px rgba(0,0,0,0.15)' },
  countryName: { fontSize: '24px', fontWeight: '700', marginBottom: '4px' },
  capital: { fontSize: '14px' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', marginBottom: '20px', borderRadius: '12px', padding: '20px' },
  stat: { textAlign: 'center' },
  statValue: { display: 'block', fontSize: '16px', fontWeight: '700', marginBottom: '4px' },
  statLabel: { fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  description: { fontSize: '15px', lineHeight: '1.7', marginBottom: '20px' },
  highlights: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' },
  highlightTag: { padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
  expandedContent: { marginTop: '20px' },
  divider: { height: '1px', marginBottom: '20px' },
  leaderInfo: { fontSize: '14px', marginBottom: '8px' },
  joinedInfo: { fontSize: '14px', marginBottom: '20px' },
  keyFactsTitle: { fontSize: '16px', fontWeight: '600', marginBottom: '12px' },
  keyFactsList: { listStyle: 'none', padding: 0, margin: 0 },
  keyFact: { fontSize: '14px', marginBottom: '8px', paddingLeft: '8px' },
  expandButton: { width: '100%', padding: '12px', border: '1px solid', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.3s ease' },
  observersGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '25px' },
  observerCard: { borderRadius: '16px', padding: '35px 30px', textAlign: 'center' },
  observerFlag: { height: '50px', width: 'auto', marginBottom: '15px', borderRadius: '6px', boxShadow: '0 2px 10px rgba(0,0,0,0.12)' },
  observerName: { fontSize: '18px', fontWeight: '600', marginBottom: '8px' },
  observerStatus: { fontSize: '13px', fontWeight: '500', display: 'block', marginBottom: '12px' },
  observerNote: { fontSize: '14px' },
};
export default MemberStates;
